<template>
  <div class="col-sm-12">
    <div class="card custom-card">

      <!-- Staff Type Selection -->
      <div class="form-group">
        <label class="block mb-3 font-semibold">Staff Type :</label>
        <div class="staff-type-container">
          <div
              v-for="staff in StaffTypeList"
              :key="staff.id"
              class="staff-type-box"
              :class="{ 'selected': selectedStaffType === staff.id }"
              @click="toggleStaffType(staff.id)"
          >
            <div class="staff-icon">
              <!-- You can replace this with actual icons based on staff type -->
              <i class="fas fa-user"></i>
            </div>
            <div class="staff-label">{{ staff.staf_type }}</div>
            <div class="selected-indicator" v-if="selectedStaffType === staff.id">
              <i class="fas fa-check"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Name Input -->
      <div class="form-group">
        <label for="name" class="block mb-2 font-semibold">Name :</label>
        <input
            type="text"
            id="name"
            v-model="name"
            placeholder="Enter name"
            class="name-input"
            :disabled="isSaving"
        />
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end action-buttons">
        <button
            type="button"
            class="btn btn-danger"
            @click="Cancel"
            :disabled="isSaving"
        >
          Cancel
        </button>
        <button
            type="button"
            class="btn btn-success save-button"
            @click="saveStaff"
            :disabled="isSaving || !isFormValid"
        >
          <span v-if="!isSaving">Save</span>
          <div v-else class="loading-indicator">
            <span class="spinner"></span>
            <span>Saving...</span>
          </div>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { fetchStaffType, saveStaffDetails } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      profileId: localStorage.getItem('profile_id'),
      reportingProfileId: localStorage.getItem('reportingL1ProfileId'),
      userCategory: localStorage.getItem('user_label'),
      router: useRouter(),
      StaffTypeList: [],
      selectedStaffType: "",
      name: "",
      isSaving: false,
    }
  },

  computed: {
    isFormValid() {
      return this.selectedStaffType && this.name.trim();
    }
  },

  created() {
    this.loadStaffType();
  },

  methods: {
    async loadStaffType() {
      try {
        const response = await fetchStaffType(this.token);
        this.StaffTypeList = response.data.data || [];
        console.log('Fetched StaffList:', this.StaffTypeList);
      } catch (error) {
        console.error('Error fetching StaffList:', error);
      }
    },

    toggleStaffType(staffId) {
      if (this.isSaving) return;

      // If clicking the already selected type, deselect it
      if (this.selectedStaffType === staffId) {
        this.selectedStaffType = "";
      } else {
        this.selectedStaffType = staffId;
      }
    },

    async saveStaff() {
      if (!this.selectedStaffType) {
        useToast().error("Please select a staff type.");
        return;
      }

      if (!this.name.trim()) {
        useToast().error("Please enter a name.");
        return;
      }

      // Set saving state
      this.isSaving = true;

      let idToUse = this.profileId;
      if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
        idToUse = this.reportingProfileId;
      }

      const payload = {
        staff_type: this.selectedStaffType,
        profile_id: idToUse,
        name: this.name,
        is_active: "1", // Always active for new staff creation
      };

      try {
        const response = await saveStaffDetails(this.token, payload);
        useToast().success("Staff added successfully!");
        this.$router.push('/distributor-staff');
      } catch (error) {
        console.error("Error response:", error.response);
        useToast().error("Failed to save staff. Please try again.");
      } finally {
        this.isSaving = false;
      }
    },

    Cancel() {
      if (!this.isSaving) {
        this.$router.push({ path: "/distributor-staff" });
      }
    }
  }
}
</script>

<style scoped lang="scss">
.custom-card {
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 24px;
  padding: 16px;
}

.staff-type-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  margin-top: 12px;
}

.staff-type-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background-color: white;

  &:hover {
    border-color: #007bff;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &.selected {
    border-color: #007bff;
    background-color: #e7f3ff;
  }
}

.staff-icon {
  font-size: 24px;
  margin-bottom: 8px;
  color: #6c757d;

  .selected & {
    color: #007bff;
  }
}

.staff-label {
  font-weight: 500;
  text-align: center;
  color: #495057;

  .selected & {
    color: #007bff;
    font-weight: 600;
  }
}

.selected-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  background-color: #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.name-input {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px;
  width: 100%;
  margin-top: 8px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.action-buttons {
  padding: 16px;
  gap: 12px;

  .btn {
    padding: 10px 20px;
    border-radius: 4px;
    font-weight: 500;
    min-width: 100px;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.save-button {
  position: relative;
  min-width: 120px;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .staff-type-container {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .custom-card {
    padding: 16px;
  }

  .form-group {
    padding: 12px;
  }
}
</style>